import { defineStore } from 'pinia'
import sortBy from 'lodash/sortBy'
import { truncate } from '~/composables/util'
import { useCacheStore } from '~/store/cache'

const hitsPerPage = 10

export const useJobOfferOverviewStore = defineStore('job-offer-overview', () => {
  const cacheStore = useCacheStore()
  const graphqlFetch = useGraphqlFetch()

  const state = {
    // Search context
    areaPreference: ref('*'), // Corresponds with location
    currentPage: ref(1),
    department: ref('*'),
    jobFunctionVolunteer: ref('*'),
    jobFunctionPaid: ref('*'),
    type: ref(undefined), // Corresponds with jobOfferType

    // Search results
    facets: ref({
      areaPreferences: {},
      departments: {},
      jobFunctionsVolunteer: {},
      jobFunctionsPaid: {},
    }),
    hits: ref([]),
    totalHits: ref(0),
  }

  state.hitsPerPage = computed(() => hitsPerPage)
  state.urlParameters = computed(() => ({
    areaPreference: state.areaPreference.value === '*' ? undefined : state.areaPreference.value,
    department: state.department.value === '*' ? undefined : state.department.value,
    jobFunctionVolunteer: state.jobFunctionVolunteer.value === '*' ? undefined : state.jobFunctionVolunteer.value,
    jobFunctionPaid: state.jobFunctionPaid.value === '*' ? undefined : state.jobFunctionPaid.value,
    page: state.currentPage.value === 1 ? undefined : state.currentPage.value,
  }))
  state.areaPreferences = computed(() => state.facets.value.areaPreferences)
  state.departments = computed(() => state.facets.value.departments)
  state.jobFunctionsVolunteer = computed(() => state.facets.value.jobFunctionsVolunteer)
  state.jobFunctionsPaid = computed(() => state.facets.value.jobFunctionsPaid)
  state.getHits = computed(() => state.hits.value.slice((state.currentPage.value - 1) * hitsPerPage, state.currentPage.value * hitsPerPage))

  function initialize({
    areaPreference,
    department,
    page,
    jobFunctionVolunteer,
    jobFunctionPaid,
    type,
  }: {
    areaPreference?: string
    department?: string
    page?: string
    jobFunctionVolunteer?: string
    jobFunctionPaid?: string
    type?: string
  }) {
    // When there are no values given, i.e. from the URL, we will simply keep the most recently used search context
    if (
      areaPreference === undefined &&
      department === undefined &&
      jobFunctionVolunteer === undefined &&
      jobFunctionPaid === undefined &&
      page === undefined &&
      type === undefined
    ) {
      return
    }

    state.areaPreference.value = areaPreference ? areaPreference : '*'
    state.department.value = department ? department : '*'
    state.jobFunctionVolunteer.value = jobFunctionVolunteer ? jobFunctionVolunteer : '*'
    state.jobFunctionPaid.value = jobFunctionPaid ? jobFunctionPaid : '*'
    state.currentPage.value = Math.max(1, page && !isNaN(Number.parseInt(page)) ? Number.parseInt(page) : 1)
    state.type.value = type ? type : undefined
  }

  function reset() {
    state.areaPreference.value = '*'
    state.department.value = '*'
    state.jobFunctionVolunteer.value = '*'
    state.jobFunctionPaid.value = '*'
    state.currentPage.value = 1
    // Note that we do not reset `type` as this depends on the page and not user input
    fetchJobOffers()
  }

  function setAreaPreference(areaPreference: string) {
    state.areaPreference.value = areaPreference ? areaPreference : '*'
  }

  function setDepartment(department: string) {
    state.department.value = department ? department : '*'
  }

  function setJobFunctionVolunteer(jobFunctionVolunteer: string) {
    state.jobFunctionVolunteer.value = jobFunctionVolunteer ? jobFunctionVolunteer : '*'
  }

  function setJobFunctionPaid(jobFunctionPaid: string) {
    state.jobFunctionPaid.value = jobFunctionPaid ? jobFunctionPaid : '*'
  }

  function setCurrentPage(page: number) {
    state.currentPage.value = Math.max(1, page && !isNaN(page) ? page : 1)
    fetchJobOffers()
  }

  function setFacets(facets: object) {
    state.facets.value = facets as any
  }

  function setHits(hits: object) {
    state.hits.value = Array.isArray(hits) ? hits : []
  }

  function setTotalHits(totalHits: number) {
    state.totalHits.value = totalHits ? totalHits : 0
  }

  async function fetchFacets() {
    const type = state.type.value
    const key = `job-offer-overview;facets;type:${type}`
    const ttl = 900

    const fetcher = async () => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query jobOfferFacets($site:[String], $section:[String], $type:[String]) {
            elasticSearch(site:$site, section:$section, jobOfferTypeFacet:$type) {
              facets(limit:128) {
                areaPreferences: location {
                  value
                }
                jobFunctionsVolunteer: jobOfferTypeVolunteer {
                  value
                }
                jobFunctionsPaid: jobOfferTypePaid {
                  value
                }
                departments: departmentTitle {
                  value
                }
              }
            }
          }`,
        variables: {
          section: 'jobOffer',
          site: 'default',
          type,
        },
      })

      return {
        areaPreferences: sortBy(
          elasticSearch.facets.areaPreferences.map(({ value }: { value: string }) => ({ value, label: truncate(value, { length: 50, omission: '...' }) })),
          ({ label }: { label: string }) => label.toLowerCase(),
        ),
        departments: sortBy(
          elasticSearch.facets.departments.map(({ value }: { value: string }) => ({ value, label: truncate(value, { length: 50, omission: '...' }) })),
          ({ label }: { label: string }) => label.toLowerCase(),
        ),
        jobFunctionsVolunteer: sortBy(
          elasticSearch.facets.jobFunctionsVolunteer.map(({ value }: { value: string }) => ({ value, label: truncate(value, { length: 50, omission: '...' }) })),
          ({ label }: { label: string }) => label.toLowerCase(),
        ),
        jobFunctionsPaid: sortBy(
          elasticSearch.facets.jobFunctionsPaid.map(({ value }: { value: string }) => ({ value, label: truncate(value, { length: 50, omission: '...' }) })),
          ({ label }: { label: string }) => label.toLowerCase(),
        ),
      }
    }

    setFacets(await cacheStore.fetch({ key, ttl, fetcher, fallback: { areaPreferences: {}, departments: {}, jobFunctionsVolunteer: {}, jobFunctionsPaid: {} } }))
  }

  async function fetchJobOffers() {
    const areaPreference = state.areaPreference.value
    const department = state.department.value
    const jobFunctionVolunteer = state.jobFunctionVolunteer.value
    const jobFunctionPaid = state.jobFunctionPaid.value
    const type = state.type.value

    const key = `job-offer-overview;hits;type:${type};areaPreference:${areaPreference};department:${department};jobFunctionVolunteer:${jobFunctionVolunteer};jobFunctionPaid:${jobFunctionPaid}`
    const ttl = 300

    const fetcher = async () => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query jobOfferHits($site:[String], $section:[String], $type:[String], $areaPreference:[String], $department:[String], $jobFunctionVolunteer:[String], $jobFunctionPaid:[String]) {
            elasticSearch(site:$site, section:$section, jobOfferTypeFacet:$type, locationFacet:$areaPreference, departmentTitleFacet:$department, jobOfferTypeVolunteerFacet:$jobFunctionVolunteer, jobOfferTypePaidFacet:$jobFunctionPaid, orderBy: "postDate desc") {
              count
              hits(limit:1024) {
                uri
              }
            }
          }`,
        variables: {
          areaPreference: areaPreference === '*' ? undefined : areaPreference,
          department: department === '*' ? undefined : department,
          jobFunctionVolunteer: jobFunctionVolunteer === '*' ? undefined : jobFunctionVolunteer,
          jobFunctionPaid: jobFunctionPaid === '*' ? undefined : jobFunctionPaid,
          section: 'jobOffer',
          site: 'default',
          type,
        },
      })

      return { hits: elasticSearch.hits.map(({ uri }: { uri: string }) => uri), totalHits: elasticSearch.count }
    }
    const { hits, totalHits } = await cacheStore.fetch({ key, ttl, fetcher, fallback: { hits: [], totalHits: 0 } })
    setHits(hits)
    setTotalHits(totalHits)
  }

  return {
    ...state,
    initialize,
    reset,
    setAreaPreference,
    setDepartment,
    setJobFunctionVolunteer,
    setJobFunctionPaid,
    setCurrentPage,
    setFacets,
    setHits,
    setTotalHits,
    fetchFacets,
    fetch: fetchJobOffers,
  }
})
